import { useCallback, useEffect, useRef, useState } from "react";
import html2pdf from "html2pdf.js";
import { useParams, useSearchParams } from "react-router-dom";
import { Download, MessageCircle, Printer } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api, dt, errMsg, money, paymentWaText, saleWaText } from "@/lib/api";
import SendReceipt from "@/components/SendReceipt";
import { useAuth } from "@/context/AuthContext";
import { ErrorBox, Loading } from "@/components/common";

function useDoc(url) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(url).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, [url]);
  return [d, err];
}

const fileName = (...parts) => parts.filter(Boolean).join("_").replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, "_");
const dayOf = (iso) => (iso || new Date().toISOString()).slice(0, 10);

function Shell({ title, number, file, wa, children }) {
  const { settings: s } = useAuth();
  const [small, setSmall] = useState(false);
  const [sp] = useSearchParams();
  const ref = useRef(null);
  const done = useRef(false);
  const pdfOpts = useCallback(() => ({ margin: 0, filename: `${file || fileName(title, number)}.pdf`, image: { type: "jpeg", quality: 0.95 },
    html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: "mm", format: small ? [80, 200] : "a5", orientation: "portrait" },
    pagebreak: { mode: "avoid-all" } }), [file, title, number, small]);
  const savePdf = useCallback(() => html2pdf().set(pdfOpts()).from(ref.current).save(), [pdfOpts]);
  const [sharing, setSharing] = useState(sp.get("send") === "1");
  const makePdf = async () => {
    const o = pdfOpts();
    const blob = await html2pdf().set(o).from(ref.current).outputPdf("blob");
    return new File([blob], o.filename, { type: "application/pdf" });
  };
  useEffect(() => {
    if (sp.get("pdf") === "1" && !done.current) {
      const t = setTimeout(() => {
        if (done.current || !ref.current) return;
        done.current = true;
        savePdf();
      }, 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [sp, savePdf]);
  return (
    <div className="min-h-screen bg-stone-200 py-6" dir="rtl">
      <style>{`@page { size: ${small ? "80mm auto" : "A5 portrait"}; margin: 5mm; }`}</style>
      <div className="no-print flex justify-center gap-2 mb-4 flex-wrap px-2">
        <button className="k-btn-primary" onClick={() => window.print()} data-testid="print-button"><Printer className="w-5 h-5" /> طباعة</button>
        <button className="k-btn-accent" onClick={savePdf} data-testid="download-pdf-button"><Download className="w-5 h-5" /> تحميل PDF</button>
        {wa && <button className="k-btn-outline text-emerald-700" onClick={() => setSharing(true)} data-testid="send-receipt-button"><MessageCircle className="w-5 h-5" /> إرسال الوصل</button>}
        <button className="k-btn-outline" onClick={() => setSmall(!small)} data-testid="toggle-receipt-size">{small ? "صيغة A5" : "صيغة طابعة وصل 80mm"}</button>
      </div>
      {wa && <SendReceipt open={sharing} onOpenChange={setSharing} makePdf={makePdf} savePdf={savePdf} wa={wa} />}
      <div ref={ref} className={`print-page shadow-xl ${small ? "receipt80" : ""}`} data-testid="print-document">
        <div className={`flex ${small ? "flex-col items-center text-center" : "items-center justify-between"} border-b-2 border-black pb-3 mb-4 gap-3`}>
          <div className="flex items-center gap-3">
            {s?.logo ? <img src={s.logo} alt="logo" className={small ? "w-12 h-12 object-contain" : "w-16 h-16 object-contain"} /> :
              <div className="w-14 h-14 rounded-xl border-2 border-black grid place-items-center font-black text-2xl font-display">ر</div>}
            <div>
              <div className="font-display font-black text-xl">{s?.org_name}</div>
              <div className="text-xs">{s?.org_address} · {s?.org_phone}</div>
            </div>
          </div>
          <div className={small ? "" : "text-left"}>
            <div className="font-display font-extrabold text-lg">{title}</div>
            <div className="num text-base" data-testid="print-doc-number">{number}</div>
          </div>
        </div>
        {children}
        <div className="mt-3 text-[10px] text-stone-500 text-center">طُبع في {dt(new Date().toISOString())}</div>
      </div>
    </div>
  );
}

const Rows = ({ rows }) => (
  <table className="w-full border-collapse mb-4 text-sm"><tbody>
    {rows.filter(Boolean).map(([k, v]) => <tr key={k}><th className="bg-stone-100 w-2/5 font-bold">{k}</th><td>{v}</td></tr>)}
  </tbody></table>
);

const Sign = ({ labels }) => (
  <div className="grid grid-cols-2 gap-8 mt-5 text-sm">{labels.map((l) => <div key={l} className="border-t border-black pt-2 text-center">{l}</div>)}</div>
);

const DocQR = ({ path, label }) => (
  <div className="flex items-center gap-3 border border-black p-2 mb-3" data-testid="receipt-qr">
    <QRCodeSVG value={`${window.location.origin}${path}`} size={84} level="M" />
    <div className="text-xs"><b>{label}</b><br />رمز لفتح العملية في التطبيق (للموظفين المخولين فقط) – ليس تصريح خروج</div>
  </div>
);

export function PrintSale() {
  const { id } = useParams();
  const { settings } = useAuth();
  const [d, err] = useDoc(`/sales/${id}`);
  if (err) return <ErrorBox text={err} />;
  if (!d) return <Loading />;
  const s = d.sale;
  const firstPaid = d.payments.filter((p) => p.status === "valid").slice(0, 1).reduce((a, p) => a + p.amount, 0);
  return (
    <Shell title="وصل البيع" number={s.number} file={fileName("وصل_بيع", s.customer_name, s.number, `كبش_${s.ear_tag}`, dayOf(s.created_at))}
      wa={{ phone: s.customer_phone, text: saleWaText(s, settings?.org_name) }}>
      <Rows rows={[["الزبون", s.customer_name], ["الهاتف", <span className="num">{s.customer_phone}</span>], ["رقم الكبش", <b className="num text-xl">{s.ear_tag}</b>],
        ["القسم", `${s.section_code} – ${s.section_name}`], ["سعر البيع", money(s.price)], s.discount > 0 && ["الخصم", `${money(s.discount)} (${s.discount_reason})`],
        ["الإجمالي", <b>{money(s.total)}</b>], ["المبلغ المدفوع عند البيع", money(firstPaid)], ["مجموع المدفوع حتى الآن", money(s.paid_total)],
        ["الرصيد المتبقي", <b>{money(s.remaining)}</b>], ["العامل المسؤول عن الاختيار", s.worker_name], ["موظف المكتب", s.clerk_name], s.category && ["الفئة", s.category], ["التاريخ", dt(s.created_at)],
        s.notes && ["ملاحظات", s.notes], s.status === "cancelled" && ["الحالة", <b>ملغاة – {s.cancel_reason}</b>]]} />
      {settings?.sale_terms && <div className="text-xs border border-black p-2 mb-3 whitespace-pre-line"><b>شروط البيع: </b>{settings.sale_terms}</div>}
      <DocQR path={`/r/${s.qr_token}`} label={`وصل البيع ${s.number}`} />
      <div className="border-2 border-black p-3 text-center font-bold" data-testid="sale-receipt-disclaimer">هذا الوصل يثبت تسجيل عملية البيع فقط، ولا يُعد تصريحًا لخروج الكبش من الحظيرة.</div>
      <Sign labels={["توقيع الزبون", "توقيع وختم المكتب"]} />
    </Shell>
  );
}

export function PrintPayment() {
  const { id } = useParams();
  const { settings } = useAuth();
  const [d, err] = useDoc(`/payments/${id}`);
  if (err) return <ErrorBox text={err} />;
  if (!d) return <Loading />;
  const p = d.payment;
  return (
    <Shell title="وصل الدفع" number={p.number} file={fileName("وصل_دفع", p.customer_name, p.number, `كبش_${p.ear_tag}`, dayOf(p.created_at))}
      wa={d.sale?.customer_phone || p.customer_phone ? { phone: d.sale?.customer_phone || p.customer_phone, text: paymentWaText(p, settings?.org_name) } : null}>
      <Rows rows={[["رقم عملية البيع", p.sale_number], ["الزبون", p.customer_name], ["رقم الكبش", <b className="num text-xl">{p.ear_tag}</b>],
        ["المبلغ المدفوع في هذه العملية", <b className="text-lg">{money(p.amount)}</b>], ["مجموع المدفوعات السابقة", money(p.prev_total)],
        ["مجموع المدفوعات بعد هذه الدفعة", money(p.new_total)], ["الرصيد المتبقي", <b>{money(p.remaining)}</b>], ["طريقة الدفع", p.method],
        p.reference && ["مرجع العملية", p.reference], ["التاريخ والوقت", dt(p.created_at)], ["الموظف", p.clerk_name],
        p.status === "cancelled" && ["الحالة", <b>دفعة ملغاة – {p.cancel_reason}</b>]]} />
      <DocQR path={`/r/${p.qr_token}`} label={`وصل الدفع ${p.number}`} />
      <div className="text-xs text-center">وصل الدفع لا يُعد تصريحًا لخروج الكبش من الحظيرة.</div>
      <Sign labels={["توقيع الزبون", "توقيع الموظف"]} />
    </Shell>
  );
}

export function PrintCustomer() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const { settings } = useAuth();
  const [rows, err] = useDoc(`/sales?customer_id=${id}&status=all`);
  if (err) return <ErrorBox text={err} />;
  if (!rows) return <Loading />;
  const ids = (sp.get("sales") || "").split(",").filter(Boolean);
  const list = ids.length ? rows.filter((s) => ids.includes(s.id)) : rows.filter((s) => s.status === "active");
  const c = list[0] || rows[0];
  const sum = list.reduce((a, s) => ({ t: a.t + s.total, p: a.p + s.paid_total, r: a.r + s.remaining }), { t: 0, p: 0, r: 0 });
  return (
    <Shell title="وصل مجمّع" number={`${list.length} كبش`} file={c && fileName("وصل_مجمع", c.customer_name, list.map((s) => s.number).join("-"), `كباش_${list.map((s) => s.ear_tag).join("-")}`, dayOf())}
      wa={c && { phone: c.customer_phone, text: [settings?.org_name || "الربيع للأضاحي", `الزبون: ${c.customer_name}`, `الكباش: ${list.map((s) => s.ear_tag).join("، ")}`, `الإجمالي: ${money(sum.t)}`, `المدفوع: ${money(sum.p)}`, `المتبقي: ${money(sum.r)}`].join("\n") }}>
      {c && <Rows rows={[["الزبون", c.customer_name], ["الهاتف", <span className="num">{c.customer_phone}</span>], ["التاريخ", dt(new Date().toISOString())]]} />}
      <table className="w-full border-collapse mb-4 text-sm" data-testid="combined-receipt-table">
        <thead><tr className="bg-stone-100"><th>رقم البيع</th><th>رقم الكبش</th><th>القسم</th><th>الإجمالي</th><th>المدفوع</th><th>المتبقي</th></tr></thead>
        <tbody>
          {list.map((s) => <tr key={s.id}><td className="num">{s.number}{s.status === "cancelled" && " (ملغاة)"}</td><td><b className="num text-lg">{s.ear_tag}</b></td><td>{s.section_name}</td><td>{money(s.total)}</td><td>{money(s.paid_total)}</td><td>{money(s.remaining)}</td></tr>)}
          <tr className="font-bold bg-stone-100"><td colSpan={3}>المجموع ({list.length})</td><td>{money(sum.t)}</td><td>{money(sum.p)}</td><td>{money(sum.r)}</td></tr>
        </tbody>
      </table>
      {settings?.sale_terms && <div className="text-xs border border-black p-2 mb-3 whitespace-pre-line"><b>شروط البيع: </b>{settings.sale_terms}</div>}
      <div className="border-2 border-black p-3 text-center font-bold">هذا الوصل يثبت تسجيل عمليات البيع فقط، ولا يُعد تصريحًا لخروج الأغنام من الحظيرة.</div>
      <Sign labels={["توقيع الزبون", "توقيع وختم المكتب"]} />
    </Shell>
  );
}

export function PrintPermits() {
  const [sp] = useSearchParams();
  const ids = (sp.get("ids") || "").split(",").filter(Boolean);
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const key = ids.join(",");
  useEffect(() => {
    Promise.all(key.split(",").filter(Boolean).map((i) => api.get(`/permits/${i}`).then((r) => r.data)))
      .then(setList).catch((e) => setErr(errMsg(e)));
  }, [key]);
  if (err) return <ErrorBox text={err} />;
  if (!list) return <Loading />;
  const c = list[0]?.permit;
  return (
    <Shell title="تصاريح خروج" number={`${list.length} كبش`} file={c && fileName("تصاريح_خروج", c.customer_name, list.map(({ permit: p }) => p.number).join("-"), `كباش_${list.map(({ permit: p }) => p.ear_tag).join("-")}`, dayOf(c.issued_at))}>
      {c && <Rows rows={[["الزبون", c.customer_name], ["الهاتف", <span className="num">{c.customer_phone}</span>]]} />}
      <table className="w-full border-collapse mb-3 text-xs" data-testid="multi-permit-table">
        <thead><tr className="bg-stone-100"><th>رمز QR</th><th>رقم الكبش</th><th>التصريح</th><th>الحالة</th></tr></thead>
        <tbody>{list.map(({ permit: p, sale }) => (
          <tr key={p.id} data-testid={`multi-permit-row-${p.number}`}>
            <td style={{ width: 92 }}><QRCodeSVG value={p.token} size={78} level="M" /></td>
            <td><b className="num text-2xl">{p.ear_tag}</b><div>{p.section_name}</div></td>
            <td className="num">{p.number}<div>{dt(p.issued_at)}</div><div>المتبقي: {money(sale?.remaining ?? p.remaining_at_issue)}</div></td>
            <td><b>{{ valid: p.delivered_at ? "صالح – سُلّم" : "صالح", used: "مستخدم – خرج", cancelled: "ملغى" }[p.status]}</b>{p.exception && <div>باستثناء: {p.exception.by_name}</div>}</td>
          </tr>))}</tbody>
      </table>
      <div className="text-xs border border-black p-2">لكل كبش رمز خاص يُستعمل مرة واحدة. يمسح عامل الحظيرة الرمز للتسليم ثم يمسحه حارس البوابة لتأكيد الخروج.</div>
      <Sign labels={["توقيع عامل الحظيرة", "توقيع حارس البوابة"]} />
    </Shell>
  );
}

export function PrintPermit() {
  const { id } = useParams();
  const { settings } = useAuth();
  const [d, err] = useDoc(`/permits/${id}`);
  if (err) return <ErrorBox text={err} />;
  if (!d) return <Loading />;
  const p = d.permit, s = d.sale;
  const st = { valid: p.delivered_at ? "صالح – سُلّم داخل الحظيرة" : "صالح – موافق على الخروج", used: "مستخدم – خرج الكبش", cancelled: "ملغى" }[p.status];
  return (
    <Shell title="تصريح خروج" number={p.number} file={fileName("تصريح_خروج", p.customer_name, p.number, `كبش_${p.ear_tag}`, dayOf(p.issued_at))}>
      <div className="flex gap-6 items-start mb-4">
        <div className="border-2 border-black p-2 bg-white" data-testid="permit-qr"><QRCodeSVG value={p.token} size={150} level="M" /></div>
        <div className="flex-1">
          <div className="text-sm">رقم الكبش</div>
          <div className="text-5xl font-black num">{p.ear_tag}</div>
          <div className={`mt-2 inline-block border-2 px-3 py-1 font-bold ${p.status === "valid" ? "border-black" : "border-black bg-stone-200 line-through"}`} data-testid="permit-print-status">{st}</div>
        </div>
      </div>
      <Rows rows={[["الزبون", p.customer_name], ["الهاتف", <span className="num">{p.customer_phone}</span>], ["القسم", p.section_name], ["رقم البيع", p.sale_number],
        ["السعر الإجمالي", money(s?.total ?? p.total)], ["المبلغ المدفوع", money(s?.paid_total ?? p.paid_at_issue)], ["الرصيد المتبقي", money(s?.remaining ?? p.remaining_at_issue)],
        p.exception && ["استثناء معتمد", `${p.exception.by_name} – ${p.exception.reason} – ${dt(p.exception.at)}`],
        ["تاريخ الإصدار", dt(p.issued_at)], ["أصدره", p.issued_by], p.expires_at && ["صالح إلى غاية", dt(p.expires_at)],
        settings?.extra_conditions && ["شروط إضافية", settings.extra_conditions], p.cancel_reason && ["سبب الإلغاء", p.cancel_reason]]} />
      <div className="text-xs border border-black p-2">يُستعمل هذا التصريح مرة واحدة فقط. يتحقق عامل الحظيرة من مطابقة رقم الكبش قبل التسليم، ويؤكد حارس البوابة الخروج الفعلي بعد مسح الرمز.</div>
      <Sign labels={["توقيع عامل الحظيرة (التسليم)", "توقيع حارس البوابة (الخروج)"]} />
    </Shell>
  );
}
